import * as React from "react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  EmptyState,
  LastUpdated,
  MultiSelect,
  PageHeader,
  StatusBadge,
  STATUS_MAP,
  Button,
  Input,
  NavRail,
} from "@ardept/design-system";
import { useJobs, useMe, type JobsFilter } from "../api/queries.js";
import type { OperatorJobDto } from "../api/types.js";
import { ENGINE_STATUS_BADGE, JOB_STATUS_VALUES, jobBadgeStatus } from "./_status.js";
import "./JobsScreen.css";
import "./screens.css";

/** Helper: format ISO date string as relative timestamp "Ns ago", "Nm ago", etc. */
function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const ms = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((now - ms) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = new Date(ms);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Helper: format ISO datetime as "DD Mon YYYY HH:MM UTC" */
function formatDateTimeUTC(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }) + " " + d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }) + " UTC";
}

// Phase 499 AC-3 — the Status filter offers exactly the engine's derived vocabulary.
// ONE source in both directions: the VALUE is the wire member (what `?status=` accepts;
// anything else is a 400 `invalid_status`), and the LABEL is resolved through the same
// ENGINE_STATUS_BADGE -> STATUS_MAP hop the badge itself uses, so a filter option can
// never read differently from the badge it selects for.
const STATUS_OPTIONS = JOB_STATUS_VALUES.map((v) => ({
  value: v,
  label: STATUS_MAP[ENGINE_STATUS_BADGE[v]].label,
}));

// View-model row for rendering
interface JobRow {
  id: string;
  idShort: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  workflowId: string;
  state: string;
  status: string;
  startedAtDisplay: string;
  lastEventAtDisplay: string;
  spendUSDDisplay: string;
  raw: OperatorJobDto;
}

function buildJobRow(job: OperatorJobDto, tenantMap: Map<string, { slug: string; displayName: string }>): JobRow {
  const tenant = tenantMap.get(job.tenant_id);
  return {
    id: job.id,
    idShort: job.id.slice(0, 8),
    tenantId: job.tenant_id,
    tenantName: tenant?.displayName ?? "Unknown tenant",
    tenantSlug: tenant?.slug ?? "?",
    workflowId: job.workflow_id,
    state: job.state,
    status: jobBadgeStatus(job),            // Phase 499: engine-derived status + fail_reason, never the DSL literal
    startedAtDisplay: formatDateTimeUTC(job.started_at),
    lastEventAtDisplay: formatRelativeTime(job.last_event_at),
    spendUSDDisplay: `$${job.aggregate_spend_usd.toFixed(2)}`,
    raw: job,
  };
}

export function JobsScreen(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // D-URL-01: parse filter from querystring: source of truth for filter state
  const filter: JobsFilter = useMemo(() => ({
    tenant: params.get("tenant")?.split(",").filter(Boolean) ?? [],
    status: params.get("status")?.split(",").filter(Boolean) ?? [],
    since: params.get("since") ?? undefined,
    until: params.get("until") ?? undefined,
    after: params.get("after") ?? undefined,
  }), [params]);

  const { data, isLoading, error, dataUpdatedAt } = useJobs(filter);
  const { data: me } = useMe();

  // Build tenant options from /me response (CON-02; tenant multi-select)
  const tenantOptions = useMemo(
    () => (me?.allowed_tenants ?? []).map((t) => ({ value: t.slug, label: t.display_name })),
    [me],
  );

  // Build tenant lookup map for view-model
  const tenantMap = useMemo(() => {
    const m = new Map<string, { slug: string; displayName: string }>();
    for (const t of me?.allowed_tenants ?? []) {
      m.set(t.id, { slug: t.slug, displayName: t.display_name });
    }
    return m;
  }, [me]);

  // Current filter values parsed from URL
  const tenantFilter = filter.tenant ?? [];
  const statusFilter = filter.status ?? [];
  const since = filter.since ?? "";
  const until = filter.until ?? "";

  function setTenantFilter(vals: string[]) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (vals.length > 0) next.set("tenant", vals.join(","));
      else next.delete("tenant");
      next.delete("after");
      return next;
    });
  }

  function setStatusFilter(vals: string[]) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (vals.length > 0) next.set("status", vals.join(","));
      else next.delete("status");
      next.delete("after");
      return next;
    });
  }

  function setSince(val: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val) next.set("since", val);
      else next.delete("since");
      next.delete("after");
      return next;
    });
  }

  function setUntil(val: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val) next.set("until", val);
      else next.delete("until");
      next.delete("after");
      return next;
    });
  }

  function resetFilters() {
    setParams(new URLSearchParams());
  }

  function loadMore() {
    if (data?.next_cursor) {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("after", data.next_cursor!);
        return next;
      });
    }
  }

  const hasFilters = tenantFilter.length > 0 || statusFilter.length > 0 || !!since || !!until;

  const jobs = data?.jobs ?? [];
  const rows = jobs.map((j) => buildJobRow(j, tenantMap));

  // Tenant summary display
  const tenantSummary = (vals: readonly string[]): React.ReactNode => {
    if (vals.length === 0) return <span className="placeholder">All tenants</span>;
    if (vals.length === tenantOptions.length) return "All tenants";
    const names = (vals as string[])
      .slice(0, 2)
      .map((v) => tenantOptions.find((t) => t.value === v)?.label?.split(" ")[0])
      .join(", ");
    const extra = vals.length > 2 ? ` +${vals.length - 2}` : "";
    return names + extra;
  };

  const statusSummary = (vals: readonly string[]): React.ReactNode => {
    if (vals.length === 0) return <span className="placeholder">All statuses</span>;
    if (vals.length === 1) return STATUS_OPTIONS.find((s) => s.value === vals[0])?.label;
    return `${STATUS_OPTIONS.find((s) => s.value === vals[0])?.label} +${vals.length - 1}`;
  };

  // Build operator display for NavRail from /me response
  const operatorDisplay = {
    name: me?.email
      ? me.email.split("@")[0]?.replace(/[._-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ?? me.email
      : "Operator",
    email: me?.email ?? "",
  };

  return (
    <div className="app">
      <NavRail active="jobs" operator={operatorDisplay} onNavigate={navigate} />
      <main className="main">
        <PageHeader
          title="Jobs"
          subtitle="Every active and recent job across your authorised tenants. Drill in for the full event log."
        />
        {dataUpdatedAt && <LastUpdated timestamp={dataUpdatedAt} />}

        {/* Filter bar */}
        <div className="filters">
          <div className="filters__group">
            <label>Tenant</label>
            <MultiSelect
              options={tenantOptions}
              value={tenantFilter}
              onChange={setTenantFilter}
              placeholder="All tenants"
              summary={tenantSummary}
            />
          </div>
          <div className="filters__group">
            <label>Status</label>
            <MultiSelect
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="All statuses"
              summary={statusSummary}
            />
          </div>
          <div className="filters__group">
            <label>Started after</label>
            <Input
              type="date"
              value={since}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSince(e.target.value)}
            />
          </div>
          <div className="filters__group">
            <label>Started before</label>
            <Input
              type="date"
              value={until}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUntil(e.target.value)}
            />
          </div>
          <div className="filters__spacer" />
          <Button variant="ghost" onClick={resetFilters}>Reset filters</Button>
        </div>

        {/* Loading state */}
        {isLoading && <div className="jobs-loading">Loading jobs...</div>}

        {/* Error state */}
        {error && !isLoading && (
          <div className="jobs-error">
            Could not load jobs. Check your connection or try refreshing.
          </div>
        )}

        {/* Table or empty state */}
        {!isLoading && !error && (
          <div className="tbl-wrap">
            {rows.length > 0 ? (
              <>
                <table className="jobs">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Tenant</th>
                      <th>Workflow</th>
                      <th>Current state</th>
                      <th>Status</th>
                      <th>Started</th>
                      <th>Last event</th>
                      <th className="num">Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} onClick={() => navigate(`/jobs/${row.id}`)}>
                        <td><span className="cell-id">{row.idShort}</span></td>
                        <td>
                          <div className="cell-tenant">
                            <b>{row.tenantName}</b>
                            <span className="slug">{row.tenantSlug}</span>
                          </div>
                        </td>
                        <td><span className="cell-wf">{row.workflowId}</span></td>
                        <td><span className="cell-state">{row.state}</span></td>
                        <td>
                          <StatusBadge status={row.status} />
                        </td>
                        <td><span className="cell-time cell-time--stack">{row.startedAtDisplay}</span></td>
                        <td><span className="cell-time">{row.lastEventAtDisplay}</span></td>
                        <td className="num"><span className="cell-spend">{row.spendUSDDisplay}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="tbl-foot">
                  <span className="count">Showing <b>{rows.length}</b> jobs</span>
                  {data?.next_cursor && (
                    <Button variant="ghost" onClick={loadMore}>Load more</Button>
                  )}
                </div>
              </>
            ) : (
              hasFilters ? (
                <EmptyState
                  iconName="search-x"
                  title="No jobs match these filters"
                  body="Try widening your tenant or status selection, or clearing the date range."
                />
              ) : (
                <EmptyState
                  iconName="search-x"
                  title="No jobs in scope yet"
                  body="Jobs will appear here once workflows are started for your tenants."
                />
              )
            )}
          </div>
        )}
      </main>
    </div>
  );
}
